/**
 * StoreSwitcher.tsx — Store/Language Switcher
 * 
 * Fetches available store views from Magento's storeConfig GraphQL query.
 * Falls back to data.stores prop if configured manually in admin.
 * On change, navigates to the store-specific URL.
 */
import React, { useEffect, useState } from 'react';
import { getGraphQLEndpoint, getGraphQLHeaders, getStoreCode } from '@agentic/magento-client/lib/storeConfig';

interface StoreView {
  code: string;
  name: string;
  locale: string;
  is_default: boolean;
}

interface StoreSwitcherProps {
  data: {
    stores?: StoreView[];
    showFlags?: boolean;
    variant?: string;
    [key: string]: unknown;
  };
  styles?: string;
  variant?: string;
}

const GET_AVAILABLE_STORES = `{
  availableStores {
    store_code store_name locale is_default_store
  }
}`;

export const StoreSwitcher: React.FC<StoreSwitcherProps> = ({ data, styles, variant }) => {
  const { showFlags: _showFlags = true } = data ?? {};
  const effectiveVariant = variant || data?.variant;
  const isBentoDark = effectiveVariant === 'bento-dark';
  const [stores, setStores] = useState<StoreView[]>(data?.stores ?? []);
  const [currentStore, setCurrentStore] = useState(getStoreCode());

  useEffect(() => {
    if (stores.length > 0) return; // Already have data from props

    interface MagentoStoreView {
      store_code: string;
      store_name: string;
      locale: string;
      is_default_store: boolean;
    }
    interface AvailableStoresResponse {
      data?: { availableStores?: MagentoStoreView[] };
    }
    void fetch(getGraphQLEndpoint(), {
      method: 'POST',
      headers: getGraphQLHeaders(),
      body: JSON.stringify({ query: GET_AVAILABLE_STORES }),
    })
      .then(r => r.json() as Promise<AvailableStoresResponse>)
      .then(json => {
        const available = json?.data?.availableStores;
        if (available && available.length > 1) {
          setStores(available.map((s: MagentoStoreView) => ({
            code: s.store_code,
            name: s.store_name,
            locale: s.locale,
            is_default: s.is_default_store,
          })));
        }
      })
      .catch(() => { /* Single store — no switcher needed */ });
  }, [stores.length]);

  // Don't render if only one store
  if (stores.length <= 1) return null;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStore = e.target.value;
    setCurrentStore(newStore);
    // Navigate — most multi-store setups use /{store_code}/ prefix
    window.location.href = `/${newStore}/`;
  };

  // Store switcher sits in the header utility row chrome — use chrome-scoped
  // text tokens with body fallback for non-chrome themes.
  const selectClass = isBentoDark
    ? 'px-3 py-1.5 text-xs bg-[color-mix(in_srgb,var(--surface-1)_3%,transparent)] border border-[color-mix(in_srgb,var(--glass-border)_5%,transparent)] text-[var(--color-header-utility-text,var(--color-header-menu-text,var(--text-secondary)))] appearance-none cursor-pointer hover:border-[var(--color-header-icon-hover,var(--color-secondary))]/20 transition-colors'
    : 'px-3 py-1.5 text-sm bg-[var(--surface-2,#1e293b)] border border-[var(--glass-border,rgba(255,255,255,0.08))] text-[var(--color-header-utility-text,var(--color-header-menu-text,var(--text-secondary,#94a3b8)))] appearance-none cursor-pointer hover:border-[var(--color-header-icon-hover,var(--color-secondary,#FB7C00))] transition-colors';

  return (
    <div className={`${styles ?? ''}`}>
      <select
        value={currentStore}
        onChange={handleChange}
        className={selectClass}
        aria-label="Select store"
      >
        {stores.map(store => (
          <option key={store.code} value={store.code}>
            {store.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default StoreSwitcher;
